/** Display names and paths for state legislative districts (sites#467), shared by
 * the /districts/ list and map. The slug rule is the graph's and the us-pages
 * renderer's: `<st>-<sd|hd>-<Census code as stored>`, lower-cased (ak-sd-00a,
 * mn-hd-01a, vt-hd-f-3, ma-sd-d06). */

export type SLDKind = 'sldu' | 'sldl';

export const SLD_CHAMBER: Record<SLDKind, 'sd' | 'hd'> = { sldu: 'sd', sldl: 'hd' };
export const SLD_LABEL: Record<SLDKind, string> = { sldu: 'State Senate', sldl: 'State House' };

const SLD_CODE = /^[0-9A-Z][0-9A-Z-]*$/;

/** A Census code without its zero padding: 055 → 55, 00A → A, 01A → 1A, 000 → 0; F-3 and D06 unchanged. */
export function naturalCode(code: string): string {
  return /^0+$/.test(code) ? '0' : code.replace(/^0+(?=[0-9A-Z])/, '');
}

/** The canonical SLD slug for a state and Census code, or null for a code with no page (ZZZ "not defined", malformed). */
export function sldSlug(state: string, kind: SLDKind, code: string): string | null {
  const st = String(state || '').toUpperCase();
  const c = String(code || '').toUpperCase();
  if (!/^[A-Z]{2}$/.test(st) || !SLD_CODE.test(c) || c === 'ZZZ') return null;
  return `${st}-${SLD_CHAMBER[kind]}-${c}`.toLowerCase();
}

/**
 * "Alaska State Senate District A", "Minnesota State House District 1A",
 * "Massachusetts Worcester and Hampshire District": the TIGER name (NAMELSAD) with
 * the state in front, else the chamber and the natural code. Zero padding after
 * "District" in a TIGER name (NJ "Assembly District 07") is dropped as well.
 */
export function sldDisplayName(stateName: string, kind: SLDKind, code: string, name?: string | null): string {
  const tiger = String(name ?? '').trim().replace(/\bDistrict 0+(?=[0-9A-Z])/, 'District ');
  const base = tiger || `${SLD_LABEL[kind]} District ${naturalCode(String(code || '').toUpperCase())}`;
  return stateName && !base.startsWith(stateName) ? `${stateName} ${base}` : base;
}
